import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import dayjs from 'dayjs';
import { Duty, OnHourDuty } from '../../shared/models/on-hour-duty.model';
import { DriverQualificationService } from '../../driver-qualification-files/service/driver-qualification.service';
import { catchError, finalize, first, switchMap } from 'rxjs/operators';
const CERTIFY_DATE_FORMAT = 'DD MMM YYYY hh mm A';
const CERTIFY_DATE_BACKEND_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
/** Backend stores at most 14 days of on-duty hours; cap UI and payload to match. */
const MAX_ON_DUTY_DAYS = 14;
@Component({
    selector: 'app-on-duty-hour',
    templateUrl: './on-duty-hour.component.html',
    styleUrls: ['./on-duty-hour.component.scss'],
    standalone: false
})
export class OnDutyHourComponent implements OnInit {
  loading: boolean;
  hoursCombo = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15', '16', '17', '18'];
  form: UntypedFormGroup;
  todayDate = new Date();
  workHourHistory: Duty[] = [];
  errorMessage: string;
  constructor(
    private readonly location: Location,
    private readonly fb: UntypedFormBuilder,
    private readonly driverQualificationService: DriverQualificationService
  ) {}

  ngOnInit(): void {
    this.form = this.createForm();
    this.loadOnDutyHours();
  }

  goBack(): void {
    if (this.loading) {
      this.location.back();
      return;
    }
    if (!this.workHourHistory?.length) {
      this.location.back();
      return;
    }
    this.ensureZeroDefaultsForSave();
    const saveModel = this.prepareModel();
    if (saveModel) {
      this.loading = true;
      this.driverQualificationService
        .updateOnDutyHours(saveModel)
        .pipe(
          catchError(error => {
            this.loading = false;
            const errorMessage = error.message;
            this.errorMessage = errorMessage;
            console.error(errorMessage);
            this.location.back();
            return error;
          })
        )
        .subscribe(() => {
          this.location.back();
        });
    } else {
      this.location.back();
    }
  }

  loadOnDutyHours(): void {
    this.loading = true;
    this.driverQualificationService
      .getOnDutyHours(this.driverQualificationService.dqfId)
      .pipe(
        first(),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe(res => {
        if (res) {
          this.populateForm(res);
        }
      });
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }
    const saveModel = this.prepareModel();
    if (saveModel) {
      this.loading = true;
      this.driverQualificationService
        .updateOnDutyHours(saveModel)
        .pipe(
          catchError(error => {
            this.loading = false;
            const errorMessage = error.message;
            this.errorMessage = errorMessage;
            console.error(errorMessage);
            return error;
          })
        )
        .subscribe(() => {
          this.loadOnDutyHours();
        });
    }
  }

  private populateForm(model: OnHourDuty) {
    if (!model) {
      return;
    }
    if (model.workedHoursHistory.length > 0) {
      this.workHourHistory = [];
      this.workHourHistory = model.workedHoursHistory
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .slice(0, MAX_ON_DUTY_DAYS);
      this.trimHourFormControlsBeyond(MAX_ON_DUTY_DAYS);
      this.ensureHourControlsExist(this.workHourHistory.length);
      for (let i = 0; i < this.workHourHistory.length; i++) {
        let name = this.getFormControlName(i);
        this.form.get(name)?.setValue(this.normalizeHourSelectValue(this.workHourHistory[i].hoursWorked));
      }
    }
    let formattedDate = dayjs(this.todayDate).format(CERTIFY_DATE_FORMAT);
    this.form.get('certifyCreatedAt').setValue(formattedDate);
    this.form.get('id').setValue(model.id);
  }

  private prepareModel(): OnHourDuty {
    const formattedDate = dayjs(this.todayDate).format(CERTIFY_DATE_BACKEND_FORMAT);

    const sortedHistory = this.workHourHistory
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, MAX_ON_DUTY_DAYS);

    let workHistoryModel: OnHourDuty = {
      id: this.form.get('id').value,
      workedHoursHistory: JSON.parse(JSON.stringify(sortedHistory)),
      certifyCreatedAt: formattedDate
    };
    for (let i = 0; i < sortedHistory.length; i++) {
      let name = this.getFormControlName(i);
      const control = this.form.get(name);
      if (control) {
        workHistoryModel.workedHoursHistory[i].hoursWorked = this.normalizeHourSelectValue(control.value);
      }
    }

    return workHistoryModel;
  }

  private createForm(): UntypedFormGroup {
    const formattedDate = dayjs(this.todayDate).format(CERTIFY_DATE_FORMAT);
    const zero = '0';

    const form = this.fb.group({
      id: [''],
      hour1: [zero, Validators.required],
      hour2: [zero, Validators.required],
      hour3: [zero, Validators.required],
      hour4: [zero, Validators.required],
      hour5: [zero, Validators.required],
      hour6: [zero, Validators.required],
      hour7: [zero, Validators.required],
      hour8: [zero],
      hour9: [zero],
      hour10: [zero],
      hour11: [zero],
      hour12: [zero],
      hour13: [zero],
      hour14: [zero],
      certifyCreatedAt: [formattedDate, Validators.required]
    });

    return form;
  }

  /** Remove dynamic hour{N} controls when capping days below a prior load (e.g. 15 → 14). */
  private trimHourFormControlsBeyond(maxDay: number): void {
    for (let i = maxDay + 1; i <= 31; i++) {
      const name = `hour${i}`;
      if (this.form.get(name)) {
        this.form.removeControl(name);
      }
    }
  }

  /**
   * Ensures hour1..hour{dayCount} exist. First 7 days required; hour8+ optional (up to MAX_ON_DUTY_DAYS).
   */
  private ensureHourControlsExist(dayCount: number): void {
    const zero = '0';
    for (let i = 1; i <= dayCount; i++) {
      const controlName = `hour${i}`;
      if (this.form.get(controlName)) {
        continue;
      }
      if (i <= 7) {
        this.form.addControl(controlName, this.fb.control(zero, Validators.required));
      } else {
        this.form.addControl(controlName, this.fb.control(zero));
      }
    }
  }

  private normalizeHourSelectValue(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') {
      return '0';
    }
    return String(value);
  }

  /** Before save on back, treat empty fields as zero so payload is complete. */
  private ensureZeroDefaultsForSave(): void {
    for (let i = 0; i < this.workHourHistory.length; i++) {
      const name = this.getFormControlName(i);
      const control = this.form.get(name);
      if (!control) {
        continue;
      }
      const v = control.value;
      if (v === null || v === undefined || v === '') {
        control.setValue('0');
      }
    }
  }

  private getFormControlName(index) {
    return `hour${index + 1}`;
  }
}
